import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'bg-white dark:bg-ink-900 border-ink-200 dark:border-ink-800 rounded-2xl border p-4 shadow-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-ink-500 dark:text-ink-400 text-sm font-semibold">{children}</h2>;
}

export function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-1">
      <CardTitle>{label}</CardTitle>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      {hint ? <p className="text-ink-500 dark:text-ink-400 text-xs">{hint}</p> : null}
    </Card>
  );
}

export function ProgressBar({ percent, label }: { percent: number; label: string }) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="bg-ink-200 dark:bg-ink-800 h-2 w-full overflow-hidden rounded-full"
    >
      <div
        className="bg-brand-500 h-full rounded-full transition-[width] duration-500"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-ink-500 dark:text-ink-400 max-w-sm text-sm">{description}</p>
      {action}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-3 p-8">
      <span className="border-ink-300 border-t-brand-500 size-6 animate-spin rounded-full border-2" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  children: ReactNode;
}) {
  const tones = {
    info: 'bg-ink-100 text-ink-700 dark:bg-ink-800 dark:text-ink-200',
    warn: 'bg-warn-400/15 text-warn-400',
    danger: 'bg-danger-400/15 text-danger-400',
  } as const;
  return <div className={cn('rounded-xl px-4 py-3 text-sm', tones[tone])}>{children}</div>;
}
